import { Dialog, Tab, TabView, useDialog } from 'components';
import DataTable, { DataTableColumn } from 'components/tables/data-table';
import { useLookups } from 'contexts';
import { FC, useCallback, useMemo, useState } from 'react';
import { UnitLookupOption } from 'types';
import './unit-options.scss';

const columns: DataTableColumn[] = [
  { field: 'code', label: 'Code', width: 80 },
  { field: 'label', label: 'Label', width: 160 },
  { field: 'active', label: 'Active', width: 60 }
];

type SectionProps = {
  title: string;
  list: UnitLookupOption[];
  save: (option: Partial<UnitLookupOption>) => Promise<void>;
  remove: (id: number) => Promise<void>;
  csvFilename: string;
};

const blankDraft = (): Partial<UnitLookupOption> => ({
  code: '',
  label: '',
  active: true
});

const LookupOptionsSection: FC<SectionProps> = ({ title, list, save, remove, csvFilename }) => {
  const { ref, openDialog, closeDialog } = useDialog({});
  const [selectedRows, setSelectedRows] = useState<UnitLookupOption[]>([]);
  const [draft, setDraft] = useState<Partial<UnitLookupOption>>(blankDraft);

  const fetchData = useCallback(() => Promise.resolve(list), [list]);

  /* seed the draft (managed fields only — sortOrder is backend-owned), then open the (stable) dialog */
  const openFor = useCallback(
    (option?: UnitLookupOption) => {
      setDraft(
        option ? { id: option.id, code: option.code, label: option.label, active: option.active } : blankDraft()
      );
      openDialog();
    },
    [openDialog]
  );

  return (
    <>
      <Dialog
        ref={ref}
        closeDialog={closeDialog}
      >
        <div className="dialog-header">
          <h3>{draft.id ? `Edit ${title}` : `New ${title}`}</h3>
        </div>
        <div className="dialog-body">
          <div>
            <p className="margin-top-16px">Code</p>
            <input type="text" value={draft.code} onChange={({ target }) => setDraft({ ...draft, code: target.value })} />
          </div>
          <div>
            <p className="margin-top-16px">Label</p>
            <input
              type="text"
              value={draft.label}
              onChange={({ target }) => setDraft({ ...draft, label: target.value })}
            />
          </div>
          {!!draft.id && (
            <div className="d-flex align-items-center margin-top-16px">
              <input
                type="checkbox"
                checked={!!draft.active}
                onChange={({ target }) => setDraft({ ...draft, active: target.checked })}
              />
              <p className="margin-left-8px mb-0">Active</p>
            </div>
          )}
          <div className="d-flex justify-content-between margin-top-16px">
            <button className="btn-outline" onClick={closeDialog}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={!draft.code || !draft.label}
              onClick={() => {
                save(draft);
                closeDialog();
              }}
            >
              <span>Save</span>
            </button>
          </div>
        </div>
      </Dialog>
      <div className="d-flex justify-content-end g-3 align-items-center mb-2">
        {!!selectedRows.length && (
          <>
            <button className="btn-danger" onClick={() => selectedRows.filter(r => !!r.id).forEach(r => remove(r.id))}>
              delete
            </button>
            <button
              className="btn-outline"
              disabled={selectedRows.length !== 1}
              onClick={() => openFor(selectedRows[0])}
            >
              edit
            </button>
          </>
        )}
        <button className="btn-primary" onClick={() => openFor()}>
          NEW OPTION
        </button>
      </div>
      <DataTable
        key={`${title}-${list.length}`}
        tableClass="t-height"
        fetchData={fetchData}
        onRowSelectionModelChange={setSelectedRows}
        initialPage={{ sortField: 'label', sortDirection: 'asc', pageSize: 25 }}
        columns={columns}
        initialColumns={columns}
        rowClassName={row => (row.active ? '' : 'deleted')}
        clientSidePagination
        csvFilename={csvFilename}
        hideTableControls
        checkboxSelection
      />
    </>
  );
};

export const UnitOptionsTab: FC = () => {
  const { unitStatusList, unitGradeList, saveUnitStatus, deleteUnitStatus, saveUnitGrade, deleteUnitGrade } =
    useLookups();

  const tabs: Tab[] = useMemo(
    () => [
      {
        label: 'Unit Status',
        content: (
          <LookupOptionsSection
            title="Unit Status"
            list={unitStatusList}
            save={saveUnitStatus}
            remove={deleteUnitStatus}
            csvFilename="UnitStatuses.csv"
          />
        )
      },
      {
        label: 'Unit Grade',
        content: (
          <LookupOptionsSection
            title="Unit Grade"
            list={unitGradeList}
            save={saveUnitGrade}
            remove={deleteUnitGrade}
            csvFilename="UnitGrades.csv"
          />
        )
      }
    ],
    [unitStatusList, unitGradeList, saveUnitStatus, deleteUnitStatus, saveUnitGrade, deleteUnitGrade]
  );

  return (
    <div id="unit-options">
      <TabView tabs={tabs} variant="embedded" />
    </div>
  );
};
